import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import { getFormattedDate, isEmpty } from '../../helpers/common';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import Calendar from 'react-calendar';
import UserPreferenceSingleton from '../../helpers/userPreferenceSingleton';
import { DEFAULT_DATE_FORMAT, ICON_SIZE } from '../../global/constants';
import { ClearIcon, ErrorText, IconDiv, InputWrapper, CalendarWrapper, Container, Footer } from './styles';
import Input from '../input';

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

interface Props {
  label: string;
  value: string | Date | null;
  inputFormat: string;
  onChange: (date: dayjs.Dayjs | null) => void;
  className?: string;
  minDate?: Date;
  calendarClassName?: string;
}

const DateTime = ({
  label,
  value,
  inputFormat = DEFAULT_DATE_FORMAT,
  onChange,
  className,
  minDate,
  calendarClassName,
  ...props
}: Props) => {
  const currentUser = UserPreferenceSingleton.getInstance().getCurrentUser();
  const dateFormat = currentUser?.dateFormat;

  const format = dateFormat || inputFormat;
  const defaultData: string = value ? getFormattedDate(new Date(value)) : '';

  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [selectedDate, setSelectedDate] = useState(
    !isEmpty(defaultData) ? dayjs(defaultData, 'DD/MM/YYYY').toDate() : undefined
  );
  const [displayValue, setDisplayValue] = useState(
    !isEmpty(defaultData) ? dayjs(defaultData, 'DD/MM/YYYY').format(format) : ''
  );

  const inputRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  const updatePosition = () => {
    if (inputRef.current && inputRef.current.offsetParent) {
      const rect = inputRef.current.getBoundingClientRect();
      const parentRect = inputRef.current.offsetParent.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const calendarHeight = 335; // Approximate height of calendar
      const calendarWidth = 335; // Approximate width of calendar
      const spaceRight = window.innerWidth - rect.right;
      const spaceLeft = rect.left;
      let left;

      // Calculate position relative to the offset parent
      const topBelow = rect.bottom - parentRect.top + 4;
      const topAbove = rect.top - parentRect.top - calendarHeight - 4;

      // if there is more space to the right, open to the right. otherwise , open to the left
      if (spaceRight >= calendarWidth || spaceRight > spaceLeft) {
        left = rect.left - parentRect.left; // Default: open to the right
      } else {
        left = rect.right - parentRect.left - calendarWidth + 47; // Open to the left
      }

      // If there's more space below, show below. Otherwise, show above
      if (spaceBelow >= calendarHeight || spaceBelow > spaceAbove) {
        setPosition({
          top: topBelow,
          left: left
        });
      } else {
        setPosition({
          top: topAbove,
          left: left
        });
      }
    }
  };

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      if (isOpen) {
        updatePosition();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  // Add this useEffect to update the date when value changes
  useEffect(() => {
    if (value && value !== 'Invalid Date') {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          const formattedDate = getFormattedDate(date);
          if (!isEmpty(formattedDate)) {
            const dayjsDate = dayjs(formattedDate, 'DD/MM/YYYY');
            if (dayjsDate.isValid()) {
              setSelectedDate(dayjsDate.toDate());
              setDisplayValue(dayjsDate.format(format));
            } else {
              setDisplayValue('Invalid Date');
            }
          }
        } else {
          setDisplayValue('Invalid Date');
        }
      } catch (error) {
        console.error('Error parsing date:', error);
        setDisplayValue('Invalid Date');
      }
    } else {
      // Clear date when value is empty
      setSelectedDate(undefined);
      setDisplayValue('');
    }
  }, [value, format]);

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setDisplayValue(dayjs(date).format(format));
    setIsOpen(false);
    onChange(dayjs(date));
  };

  const handleCalendarClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    // Only prevent closing for navigation
    if (target.closest('.react-calendar__navigation')) {
      e.stopPropagation();
    }
  };

  const handleClear = useCallback(() => {
    setSelectedDate(undefined);
    setDisplayValue('');
    onChange(null);
  }, [onChange]);

  const handleTodayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const today = new Date();
    setSelectedDate(today);
    setDisplayValue(dayjs(today).format(format));
    onChange(dayjs(today));
    setIsOpen(false);
  };

  const handleInputClick = () => {
    if (!isOpen) {
      updatePosition();
    }
    setIsOpen(!isOpen);
  };

  const renderInputRightElement = useMemo(() => {
    return (
      <>
        <IconDiv>
          <SVGIcon
            name='datepicker-icon'
            width={ICON_SIZE.LG}
            height={ICON_SIZE.LG}
            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
            fill='var(--text-secondary)'
          />
        </IconDiv>
        {displayValue && (
          <ClearIcon
            role='button'
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}>
            <SVGIcon
              name='new-close-icon'
              width={ICON_SIZE.LG}
              height={ICON_SIZE.LG}
              viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
              fill='var(--text-secondary)'
            />
          </ClearIcon>
        )}
      </>
    );
  }, [displayValue, handleClear]);

  return (
    <Container className={className}>
      <InputWrapper ref={inputRef}>
        <Input
          value={displayValue}
          placeHolder={label}
          readOnly
          onClick={handleInputClick}
          rightElement={renderInputRightElement}
        />
      </InputWrapper>
      {isOpen && (
        <CalendarWrapper
          role='button'
          ref={calendarRef}
          className={calendarClassName}
          top={position.top}
          left={position.left}
          onClick={handleCalendarClick}>
          <Calendar
            onChange={handleDateSelect}
            value={selectedDate}
            className='custom-calendar'
            onClickDay={(date) => {
              handleDateSelect(date);
            }}
            tileClassName={({ date }) => {
              const classes = ['custom-datepicker-day'];
              if (dayjs(date).isSame(dayjs(), 'day')) {
                classes.push('today');
              }
              if (selectedDate && dayjs(date).isSame(dayjs(selectedDate), 'day')) {
                classes.push('selected');
              }
              return classes.join(' ');
            }}
            formatShortWeekday={(locale, date) => {
              return ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'][date.getDay()];
            }}
            minDate={minDate}
            navigationLabel={({ date }) => dayjs(date).format('MMM YYYY')}
            prevLabel={
              <SVGIcon
                name='calender-prevLabel'
                width={ICON_SIZE.SM}
                height={ICON_SIZE.SM}
                viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
              />
            }
            nextLabel={
              <SVGIcon
                name='calender-nextLabel'
                width={ICON_SIZE.SM}
                height={ICON_SIZE.SM}
                viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
              />
            }
            prev2Label={
              <SVGIcon
                name='calender-prev2Label'
                width={ICON_SIZE.SM}
                height={ICON_SIZE.SM}
                viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
              />
            }
            next2Label={
              <SVGIcon
                name='calender-next2Label'
                width={ICON_SIZE.SM}
                height={ICON_SIZE.SM}
                viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
              />
            }
          />
          {displayValue === 'Invalid Date' && <ErrorText>Invalid date format</ErrorText>}
          <Footer role='button' onClick={(e) => handleTodayClick(e)}>
            <p>Today</p>
          </Footer>
        </CalendarWrapper>
      )}
    </Container>
  );
};

export default DateTime;
DateTime.propTypes = {
  label: PropTypes.string,
  value: PropTypes.instanceOf(Date),
  onChange: PropTypes.func,
  inputFormat: PropTypes.string
};
